/**
 * NaamDarbar — Global Auth Modal Context
 *
 * Provides a single auth modal that any component can trigger via
 * `openAuthModal()`. Avoids duplicate auth modals across the app.
 */

import { createContext, useContext, useState, useCallback, useMemo, lazy, Suspense, type ReactNode } from 'react';

// Lazy — AuthModal pulls in libphonenumber-js (~37 kB gzip); most visitors never open it.
const AuthModal = lazy(() => import('../components/auth/AuthModal'));

interface AuthModalContextValue {
  /** Open the global auth modal. Optional callback fires on successful sign-in. */
  openAuthModal: (onSuccess?: () => void) => void;
  /** Close the global auth modal. */
  closeAuthModal: () => void;
  /** Whether the auth modal is currently open. */
  isAuthModalOpen: boolean;
}

const AuthModalContext = createContext<AuthModalContextValue | undefined>(undefined);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [successCb, setSuccessCb] = useState<(() => void) | null>(null);

  const openAuthModal = useCallback((onSuccess?: () => void) => {
    setSuccessCb(() => onSuccess ?? null);
    setIsOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsOpen(false);
    setSuccessCb(null);
  }, []);

  const handleSuccess = useCallback(() => {
    successCb?.();
    closeAuthModal();
  }, [successCb, closeAuthModal]);

  const value = useMemo(
    () => ({ openAuthModal, closeAuthModal, isAuthModalOpen: isOpen }),
    [openAuthModal, closeAuthModal, isOpen],
  );

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      {isOpen && (
        <Suspense fallback={null}>
          <AuthModal onClose={closeAuthModal} onSuccess={handleSuccess} />
        </Suspense>
      )}
    </AuthModalContext.Provider>
  );
}

export function useAuthModal(): AuthModalContextValue {
  const ctx = useContext(AuthModalContext);
  if (!ctx) throw new Error('useAuthModal must be used within AuthModalProvider');
  return ctx;
}
